import { useState, useCallback } from 'react';

interface UseDragDropOptions {
    onFilesDropped: (files: File[]) => void;
}

interface UseDragDropReturn {
    isDragOver: boolean;
    dragHandlers: {
        onDragOver: (e: React.DragEvent) => void;
        onDragLeave: (e: React.DragEvent) => void;
        onDrop: (e: React.DragEvent) => void;
    };
}

/**
 * Simple hook for drag-and-drop file handling.
 * Manages isDragOver state and provides event handlers.
 */
export function useDragDrop({ onFilesDropped }: UseDragDropOptions): UseDragDropReturn {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Prevent false triggers when dragging over child elements
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            onFilesDropped(files);
        }
    }, [onFilesDropped]);

    return {
        isDragOver,
        dragHandlers: {
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop
        }
    };
}
